import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Container, Row, Col } from 'react-bootstrap';

import { groceryCategories } from '../data/categories';
import { isStoreOpen } from '../utils/isStoreOpen';
import { fetchGroceryStores } from '../api/apiService';

// Import the new CSS file
import '../styles/Groceries.css'; 

const Groceries = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [groceryStores, setGroceryStores] = useState(location.state?.groceryStores || []);
    const [filtered, setFiltered] = useState(location.state?.groceryStores || []);
    const [activeCat, setActiveCat] = useState('All');
    const [loading, setLoading] = useState(!location.state?.groceryStores);

    // --- Arrow Logic ---
    const scrollRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const listRef = useRef(null);

    // --- 1. Data Fetching ---
    useEffect(() => {
        if (!location.state?.groceryStores) {
            const loadGroceryStores = async () => {
                setLoading(true);
                try {
                    const response = await fetchGroceryStores();
                    const arr = Array.isArray(response.data.groceryStores) ? response.data.groceryStores : [];
                    setGroceryStores(arr);
                    setFiltered(arr);
                } catch (error) {
                    console.error('Error fetching grocery stores:', error);
                } finally {
                    setLoading(false);
                }
            };
            loadGroceryStores();
        }
    }, [location.state]);

    // --- 2. Filter Logic ---
    const handleCategory = (cat) => {
        setActiveCat(cat);
        if (cat === 'All') {
            setFiltered(groceryStores);
        } else {
            setFiltered(
                groceryStores.filter(g => {
                    const lower = cat.toLowerCase();
                    return (
                        (g.name && g.name.toLowerCase().includes(lower)) ||
                        (g.category && g.category.toLowerCase().includes(lower)) ||
                        (g.type && g.type.toLowerCase().includes(lower)) ||
                        (Array.isArray(g.items) && g.items.some(item =>
                            (item.name && item.name.toLowerCase().includes(lower)) ||
                            (item.category && item.category.toLowerCase().includes(lower))
                        ))
                    );
                })
            );
        }
    };
    
    // --- 3. Scroll Logic (Updated for Responsiveness) ---
    const scrollCategory = (direction) => {
        const container = scrollRef.current;
        if (!container) return;
        const scrollAmount = 250;
        container.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const container = scrollRef.current;
        if (!container) return;
        
        const tolerance = 2;
        setShowLeftArrow(container.scrollLeft > 10);
        
        const isAtEnd = (container.scrollLeft + container.clientWidth) >= (container.scrollWidth - tolerance);
        setShowRightArrow(!isAtEnd);
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (container) {
            handleScroll(); // Initial check
            
            // Listen for scroll AND resize (orientation change)
            container.addEventListener('scroll', handleScroll);
            window.addEventListener('resize', handleScroll);
            
            return () => {
                container.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, []);

    const sortedFiltered = [...filtered].sort((a, b) => {
        const aOpen = isStoreOpen(
    a.operatingHours || a.timings,
    a.location?.country
);
        const bOpen = isStoreOpen(
    b.operatingHours || b.timings,
    b.location?.country
);
        if (aOpen === bOpen) return 0;
        return aOpen ? -1 : 1;
    });


    if (loading) {
        return <div style={{ padding: '4rem', textAlign: 'center', fontSize: '1.2rem', color: '#666' }}>Loading grocery stores...</div>;
    }
    
    return (
        <Container className="groceries-container">
            
            {/* Header Section */}
            <div className="groceries-header">
                <button
                    onClick={() => navigate(-1)}
                    className="back-btn"
                    aria-label="Go back"
                    type="button"
                >
                    <FaChevronLeft />
                </button>
                <h2 className="page-title">Grocery Stores</h2>
            </div>

            {/* Category Scroller */}
            <div className="category-wrapper">
                {showLeftArrow && (
                    <button 
                        onClick={() => scrollCategory('left')} 
                        className="scroll-arrow left"
                        aria-label="Scroll left"
                    >
                        <FaChevronLeft size={14} />
                    </button>
                )}
                
                <div ref={scrollRef} className="category-list">
                    {/* 'All' Pill */}
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleCategory('All')}
                        className={`cat-pill all`}
                        style={{
                            background: activeCat === 'All' ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : '#eee',
                            color: activeCat === 'All' ? '#fff' : '#222',
                            boxShadow: activeCat === 'All' ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                        }}
                    >
                        🛒 All Stores
                    </motion.div>

                    {/* Dynamic Categories */}
                    {groceryCategories.map((cat, i) => (
                        <motion.div
                            key={i}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleCategory(cat.alt)}
                            className="cat-pill item"
                            style={{
                                background: activeCat === cat.alt ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' : '#fff',
                                boxShadow: activeCat === cat.alt ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                                border: activeCat === cat.alt ? '2px solid #e52e71' : '1px solid #eee',
                            }}
                        >
                            <img 
                                src={cat.src} 
                                alt={cat.alt} 
                                className="cat-img"
                                style={{ border: activeCat === cat.alt ? '2.5px solid #e52e71' : '2px solid #eee' }}
                            />
                            <div className="cat-name" style={{ color: activeCat === cat.alt ? '#e52e71' : '#222' }}>
                                {cat.alt}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {showRightArrow && (
                    <button 
                        onClick={() => scrollCategory('right')} 
                        className="scroll-arrow right"
                        aria-label="Scroll right"
                    >
                        <FaChevronRight size={14} />
                    </button>
                )}
            </div>

            {/* Grid System */}
            <div ref={listRef}>
                {sortedFiltered.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: '1.2rem', padding: '3rem 0', width: '100%' }}>
                        No stores found for this category.
                    </div>
                ) : (
                    <Row className="g-4">
                        {sortedFiltered.map(g => {
                            let categories = [];
                            if (Array.isArray(g.category)) {
                                categories = g.category;
                            } else if (typeof g.category === 'string') {
                                categories = g.category.split(',').map(c => c.trim()).filter(Boolean);
                            } else if (g.category) {
                                categories = [g.category];
                            }
                            const categoryDisplay = categories.slice(0, 2).join(', ');
                            const moreCategories = categories.length > 2;
                            const open = isStoreOpen(
                                g.operatingHours || g.timings,
                                g.location?.country
                            );

                            return (
                                <Col key={g._id} xs={12} md={6} lg={4} xl={3}>
                                    <motion.div whileHover={{ y: -5 }} className="grocery-card">
                                        <Link to={`/grocery-stores/${g.slug || g._id || g.id}`} className="card-link">
                                            
                                            {/* Delivery Badge */}
                                            {g.deliveryTime && (
                                                <div className="badge-delivery">
                                                    <svg width="14" height="14" fill="none" stroke="#e05260" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    {g.deliveryTime}
                                                </div>
                                            )}

                                            {/* Status Badge */}
                                            <div className={`badge-status ${open ? 'open' : 'closed'}`}>
                                                {open ? 'Opened' : 'Closed'}
                                            </div>
                                            
                                            {/* Image */}
                                            <img
                                                src={g.image || 'https://via.placeholder.com/240'}
                                                alt={g.name}
                                                className="card-image"
                                            />
                                            
                                            {/* Name */}
                                            <div className="card-name" title={g.name}>
                                                {g.name}
                                            </div>
                                            
                                            {/* Category */}
                                            <div className="card-category">
                                                {categoryDisplay}
                                                {moreCategories && <span style={{ opacity: 0.7, marginLeft: 4 }}>+ more</span>}
                                                {!categoryDisplay && (g.category || 'Grocery')}
                                            </div>
                                            
                                            <div className="spacer" />
                                            
                                            {/* CTA Button */}
                                            <motion.button
                                                whileTap={{ scale: 0.95 }}
                                                className="btn-view-items"
                                                style={{
                                                    background: open ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : 'linear-gradient(90deg, #e52e71 0%, #ff8a00 100%)', // Original logic kept
                                                    cursor: 'pointer'
                                                }}
                                                tabIndex={-1}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    navigate(`/grocery-stores/${g._id || g.id}`);
                                                }}
                                            >
                                                See Items
                                            </motion.button>
                                        </Link>
                                    </motion.div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
        </Container>
    );
};

export default Groceries;